/**
 * T355 / T356 client — IMAP + Drive integration hooks.
 *
 * Wraps the integrations API surface (status + config + test buttons).
 * Sensitive fields come back as `{encrypted: true, setAt}` markers so
 * the form can show "set at <date>" without re-displaying the secret.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api';
import { integrationKeys } from './keys';
import { useActiveTenantId } from '../../hooks/useTenantContext';

export interface ImapStatusDto {
  currentStatus: 'ok' | 'retrying' | 'failed' | 'never_ran';
  consecutiveFailures: number;
  lastAttemptAt: string | null;
  lastSuccessfulSyncAt: string | null;
  nextRetryAt: string | null;
  lastErrorMessage: string | null;
  messagesProcessedTotal: number;
}

export interface EncryptedMarker {
  encrypted: true;
  setAt: string;
}

export interface ImapConfigDto {
  imapHost: string | null;
  imapPort: number | null;
  imapUser: string | null;
  imapMailbox: string;
  imapSyncIntervalMinutes: number;
  imapEmailAddress: string | null;
  imapPassword: EncryptedMarker | null;
}

export interface ImapConfigInput {
  imapHost?: string | null;
  imapPort?: number | null;
  imapUser?: string | null;
  imapPassword?: string | null;
  imapMailbox?: string | null;
  imapSyncIntervalMinutes?: number | null;
  imapEmailAddress?: string | null;
}

export interface DriveStatusDto {
  configured: boolean;
  folderId: string | null;
}

export interface DriveConfigDto {
  driveFolderId: string | null;
  driveServiceAccountJson: EncryptedMarker | null;
}

export interface DriveConfigInput {
  driveFolderId?: string | null;
  driveServiceAccountJson?: string | null;
}

export interface ImapTestResult {
  attempted: boolean;
  imported: number;
  dropped: number;
}

export interface DriveTestResult {
  attempted: boolean;
  imported: number;
  filesSeen: number;
  errors: string[];
}

export const integrationsApi = {
  imapStatus: () => api.get<ImapStatusDto>('/integrations/imap/status'),
  imapConfig: () => api.get<ImapConfigDto>('/integrations/imap/config'),
  patchImapConfig: (input: ImapConfigInput) =>
    api.patch<{ ok: true }>('/integrations/imap/config', input),
  imapTest: () => api.post<ImapTestResult>('/integrations/imap/test', {}),
  driveStatus: () => api.get<DriveStatusDto>('/integrations/drive/status'),
  driveConfig: () => api.get<DriveConfigDto>('/integrations/drive/config'),
  patchDriveConfig: (input: DriveConfigInput) =>
    api.patch<{ ok: true }>('/integrations/drive/config', input),
  driveTest: () => api.post<DriveTestResult>('/integrations/drive/test', {}),
};

export function useImapStatus() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: integrationKeys.imapStatus(tenantId),
    queryFn: () => integrationsApi.imapStatus(),
    enabled: tenantId !== null,
    refetchInterval: 30_000,
  });
}

export function useImapConfig() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: integrationKeys.imapConfig(tenantId),
    queryFn: () => integrationsApi.imapConfig(),
    enabled: tenantId !== null,
  });
}

export function usePatchImapConfig() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: ImapConfigInput) => integrationsApi.patchImapConfig(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.imapConfig(tenantId) });
      qc.invalidateQueries({ queryKey: integrationKeys.imapStatus(tenantId) });
    },
  });
}

export function useImapTest() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: () => integrationsApi.imapTest(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.imapStatus(tenantId) });
    },
  });
}

export function useDriveStatus() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: integrationKeys.driveStatus(tenantId),
    queryFn: () => integrationsApi.driveStatus(),
    enabled: tenantId !== null,
  });
}

export function useDriveConfig() {
  const tenantId = useActiveTenantId();
  return useQuery({
    queryKey: integrationKeys.driveConfig(tenantId),
    queryFn: () => integrationsApi.driveConfig(),
    enabled: tenantId !== null,
  });
}

export function usePatchDriveConfig() {
  const qc = useQueryClient();
  const tenantId = useActiveTenantId();
  return useMutation({
    mutationFn: (input: DriveConfigInput) => integrationsApi.patchDriveConfig(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: integrationKeys.driveConfig(tenantId) });
      qc.invalidateQueries({ queryKey: integrationKeys.driveStatus(tenantId) });
    },
  });
}

export function useDriveTest() {
  return useMutation({
    mutationFn: () => integrationsApi.driveTest(),
  });
}
